import React, { useState, useEffect, useCallback, memo } from "react";
import styled from "styled-components";
import renderIf from "render-if";

import { sendCode, verifyCode } from "../data/verification";
import SendVerification, {
  SendVerificationSkeleton,
} from "../components/SendVerification";

import ConfirmVerification from "../components/ConfirmVerification";
import { Typography } from "@material-ui/core";

const Wrapper = styled.div`
  margin-bottom: 10px;
`;

const VerificationSuccessful = styled(Typography).attrs({
  variant: "subtitle1",
  color: "primary",
})`
  && {
    margin-top: 20px;
  }
`;

const HumanVerificationContainer = memo(
  ({ name, value, onChange, ...props }) => {
    const { valid } = value;

    const [error, setError] = useState();
    const [verification, setVerification] = useState({});

    const sendTo = props[verification.type];

    const resetVerification = useCallback(() => {
      setVerification({});
      onChange({ target: { name, value: { valid: false } } });
    }, [name, setVerification, onChange]);

    useEffect(() => {
      if (verification.type && !sendTo) {
        resetVerification();
        setError(`Please provide your ${verification.type} above.`);
      }

      if (sendTo && !verification.guid) {
        const sentTo = sendTo;
        sendCode({ [verification.type]: sentTo })
          .then((guid) => setVerification({ ...verification, guid, sentTo }))
          .catch((err) => {
            resetVerification();
            setError(
              `Error sending verification code to ${verification.type}, please try again`
            );
          });
        return;
      }

      if (verification.guid && verification.sentTo !== sendTo) {
        resetVerification();
        return;
      }
    }, [verification, sendTo, resetVerification]);

    const handleSendCode = async ({ target: { name } }) => {
      setError();
      setVerification({
        type: name,
        verified: false,
        guid: undefined,
        isVerified: false,
      });
    };

    const handleUserCode = useCallback(
      async ({ target: { value: code = "" } }) => {
        const { guid } = verification;
        const result = await verifyCode({ guid, code: code.trim() });

        if (result !== valid) {
          onChange({ target: { name, value: { valid: result, code } } });
          setVerification({ ...verification, verified: result });
        }
      },
      [name, valid, onChange, verification, setVerification]
    );

    return (
      <Wrapper {...props}>
        {renderIf(!verification.guid && !sendTo)(
          <SendVerification onClick={handleSendCode} error={error} />
        )}
        {renderIf(!verification.guid && sendTo)(<SendVerificationSkeleton />)}
        {renderIf(verification.guid && !verification.verified)(
          <ConfirmVerification
            onResend={resetVerification}
            onChange={handleUserCode}
          />
        )}
        {renderIf(verification.verified)(
          <VerificationSuccessful>
            Verification Successful!
          </VerificationSuccessful>
        )}
      </Wrapper>
    );
  }
);

const HumanVerification = ({ field, form, ...props }) => {
  const { onChange, name, value } = field;
  const { values } = form;
  const { email, phone } = values;

  return (
    <HumanVerificationContainer
      name={name}
      value={value}
      email={email}
      phone={phone}
      onChange={onChange}
      {...props}
    />
  );
};

export default HumanVerification;
